// Iter 145 — Cold-start league finder (admin only).
//
// Surfaces leagues with high user engagement (schedule clicks) but
// low settled-prediction coverage.  gap_score = clicks / (settled+1)
// — the higher the score, the louder the demand vs ingestion mismatch.
//
// Endpoint: GET /api/admin/cold-start-leagues?days=30&limit=20

import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import TopNav from "../components/TopNav";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Loader2, Database, Flame, RefreshCcw, Zap, Check } from "lucide-react";

const WINDOWS = [7, 14, 30, 60, 90];

const gapTone = (score) =>
  score >= 1
    ? "bg-rose-500/10 text-rose-300 border-rose-500/30"
    : score >= 0.3
    ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
    : "bg-white/[0.04] text-muted-foreground border-white/10";

const AdminDataGapsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [autoSync, setAutoSync] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState(null);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setData(null);
    axios
      .get(
        `${API}/admin/cold-start-leagues?days=${days}&limit=30&auto_sync=${autoSync ? "true" : "false"}&threshold=2.0`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((r) => {
        setData(r.data);
        if (r.data.auto_sync_fired_count > 0) {
          toast.success(
            `Auto-synced ${r.data.auto_sync_fired_count} cold-start league${r.data.auto_sync_fired_count === 1 ? "" : "s"}`,
          );
        }
      })
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          toast.error("Admin access required");
          navigate("/");
        } else {
          toast.error("Couldn't load cold-start leagues");
        }
      })
      .finally(() => setLoading(false));
  }, [days, autoSync, token, navigate]);

  useEffect(() => {
    if (user && user.is_admin === false) {
      toast.error("Admin access required");
      navigate("/");
      return;
    }
    load();
  }, [load, user, navigate]);

  const onManualSync = async (leagueId, name) => {
    if (!token || syncingId) return;
    setSyncingId(leagueId);
    try {
      const r = await axios.post(
        `${API}/admin/cold-start-leagues/${leagueId}/sync`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data.triggered) {
        const res = r.data.result || {};
        toast.success(
          `${name || "League"} synced — fetched ${res.fetched || 0}, upserted ${(res.inserted || 0) + (res.updated || 0)}`,
        );
        // Refresh the table so the new last_synced_at shows up.
        await load();
      } else {
        toast.info(r.data.reason || "Sync skipped (cooldown active)");
      }
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncingId(null);
    }
  };

  const leagues = data?.leagues || [];
  const fmtAgo = (iso) => {
    if (!iso) return null;
    try {
      const ms = Date.now() - new Date(iso).getTime();
      const h = ms / 3600000;
      if (h < 1) return `${Math.round(h * 60)}m ago`;
      if (h < 24) return `${Math.round(h)}h ago`;
      return `${Math.round(h / 24)}d ago`;
    } catch {
      return null;
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <header className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1
              className="text-3xl font-black tracking-tight flex items-center gap-2"
              data-testid="data-gaps-title"
            >
              <Database className="w-6 h-6 text-amber-400" />
              Data Gaps · Cold-start leagues
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Leagues where users click on schedule fixtures but few
              settled markets exist yet.  Higher gap_score = stronger
              demand vs ingestion mismatch — prioritise these for
              upstream data wiring.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground bg-white/[0.03] border border-white/10 rounded-full px-3 py-1.5 cursor-pointer hover:bg-white/[0.06]"
              data-testid="data-gaps-auto-sync-toggle"
            >
              <input
                type="checkbox"
                checked={autoSync}
                onChange={(e) => setAutoSync(e.target.checked)}
                className="accent-emerald-500"
              />
              <Zap className="w-3 h-3 text-amber-300" />
              Auto-sync ≥ 2.0
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              data-testid="data-gaps-refresh"
            >
              <RefreshCcw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
        </header>

        {/* Window picker */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-bold mr-1">
            Window
          </span>
          {WINDOWS.map((w) => (
            <Button
              key={w}
              variant={w === days ? "default" : "outline"}
              size="sm"
              onClick={() => setDays(w)}
              data-testid={`data-gaps-window-${w}`}
              className="rounded-full"
            >
              {w}d
            </Button>
          ))}
        </div>

        {/* Headline strip */}
        {data && (
          <div
            className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5"
            data-testid="data-gaps-headline"
          >
            <Card className="border-white/10">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold">
                  Total clicks
                </p>
                <p className="text-2xl font-black font-tabular mt-1">
                  {data.total_clicks.toLocaleString()}
                </p>
              </CardContent>
            </Card>
            <Card className="border-white/10">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold">
                  Unique leagues
                </p>
                <p className="text-2xl font-black font-tabular mt-1">
                  {data.total_leagues}
                </p>
              </CardContent>
            </Card>
            <Card className="border-white/10 md:col-span-1 col-span-2">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold flex items-center gap-1">
                  <Flame className="w-3 h-3 text-amber-400" />
                  Hottest gap
                </p>
                <p className="text-base font-bold mt-1 truncate">
                  {leagues[0]?.league_name || "—"}
                </p>
                <p className="text-xs text-muted-foreground">
                  gap {leagues[0]?.gap_score ?? "—"} · {leagues[0]?.clicks ?? 0} clicks
                </p>
              </CardContent>
            </Card>
            <Card className="border-white/10 md:col-span-3 col-span-2">
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold flex items-center gap-1">
                    <Zap className="w-3 h-3 text-amber-300" />
                    Auto-sync fired this run
                  </p>
                  <p
                    className="text-2xl font-black font-tabular mt-1"
                    data-testid="data-gaps-autofired-count"
                  >
                    {data.auto_sync_fired_count}
                    <span className="text-xs text-muted-foreground font-normal ml-2">
                      league{data.auto_sync_fired_count === 1 ? "" : "s"} ≥ {data.auto_sync_threshold ?? 2.0} gap
                    </span>
                  </p>
                </div>
                <p className="text-[11px] text-muted-foreground max-w-xs text-right">
                  Auto-sync runs background <code className="text-amber-300">sync_league_fixtures(next=50)</code> for any league crossing the threshold. 24h cooldown per league prevents over-firing.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {loading && (
          <div
            className="flex items-center gap-2 text-muted-foreground py-8"
            data-testid="data-gaps-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        )}

        {!loading && !leagues.length && (
          <Card className="border-white/10" data-testid="data-gaps-empty">
            <CardContent className="p-8 text-center">
              <p className="text-base font-semibold mb-1">No engagement yet</p>
              <p className="text-sm text-muted-foreground">
                Schedule clicks need to accumulate before this report
                becomes meaningful.  Try a longer window.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        {!loading && leagues.length > 0 && (
          <Card className="border-white/10" data-testid="data-gaps-table">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold border-b border-white/10">
                    <tr>
                      <th className="text-left p-3">League</th>
                      <th className="text-right p-3">Clicks</th>
                      <th className="text-right p-3 hidden md:table-cell">Unique matches</th>
                      <th className="text-right p-3">Settled</th>
                      <th className="text-right p-3">Gap</th>
                      <th className="text-right p-3">Sync</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leagues.map((lg) => {
                      const isSyncing = syncingId === lg.league_id;
                      const meetsThreshold = lg.gap_score >= (data?.auto_sync_threshold ?? 2.0);
                      const synced = !!lg.last_synced_at;
                      const ago = fmtAgo(lg.last_synced_at);
                      return (
                        <tr
                          key={`${lg.league_id}-${lg.league_name}`}
                          className="border-b border-white/5 last:border-b-0"
                          data-testid={`data-gaps-row-${lg.league_id}`}
                        >
                          <td className="p-3">
                            <div className="font-semibold truncate flex items-center gap-1.5">
                              {lg.league_name || `League #${lg.league_id}`}
                              {lg.auto_sync_fired && (
                                <span
                                  className="inline-flex items-center gap-0.5 text-[9px] uppercase font-bold tracking-[0.14em] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-1.5 py-0.5"
                                  data-testid={`data-gaps-autofired-${lg.league_id}`}
                                  title="Auto-synced this run"
                                >
                                  <Zap className="w-2.5 h-2.5" /> Auto
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                              {lg.country || "—"} · id {lg.league_id}
                              {ago && <span className="ml-1 text-emerald-300/80">· synced {ago}</span>}
                            </div>
                          </td>
                          <td className="p-3 text-right font-tabular">{lg.clicks}</td>
                          <td className="p-3 text-right font-tabular hidden md:table-cell">
                            {lg.unique_match_clicks}
                          </td>
                          <td className="p-3 text-right font-tabular">{lg.settled_markets}</td>
                          <td className="p-3 text-right">
                            <Badge
                              variant="outline"
                              className={`font-tabular ${gapTone(lg.gap_score)}`}
                            >
                              {lg.gap_score}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">
                            <Button
                              variant={meetsThreshold ? "default" : "outline"}
                              size="sm"
                              disabled={isSyncing || !!syncingId}
                              onClick={() => onManualSync(lg.league_id, lg.league_name)}
                              data-testid={`data-gaps-sync-${lg.league_id}`}
                              className="h-7 px-2 text-[11px]"
                            >
                              {isSyncing ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : synced && !meetsThreshold ? (
                                <>
                                  <Check className="w-3 h-3 mr-1" /> Synced
                                </>
                              ) : (
                                <>
                                  <Zap className="w-3 h-3 mr-1" /> Sync now
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminDataGapsPage;
